import { Navigate, useNavigate } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import Header from '../components/Header'
import { useI18n } from '../i18n/useI18n'
import { isRideEnabled } from '../lib/features'

// 配車リンク。しおりのタクシー区間 (#1245, domains/plan/trip/presets.ts) と同じ
// 遷移先。Uber はユニバーサルリンクで、アプリ未導入でも m.uber.com の Web 配車に
// 繋がる。GO は公開ディープリンクが無いため公式 LP (ストア誘導)。
const UBER_PICKUP_URL = 'https://m.uber.com/ul/?action=setPickup&pickup=my_location'
const TAXI_GO_URL = 'https://go.goinc.jp/'

/**
 * #1619 タクシー配車ハブ。外部配車 (Uber / GO) への入口と、タクシー会社協業後の
 * アプリ内配車「YOKOSOライド」の準備中プレビューを見せる。
 *
 * 準備中セクションは営業材料 (協業するとこうなる) を兼ねるが、**「準備中」を
 * 必ず明記しボタンは disabled** — 動くように見せる虚偽表示はしない
 * (CLAUDE.md「モックデモではない」原則との整合)。協業成立後の本実装は別 issue。
 */
export default function RideHub() {
  const navigate = useNavigate()
  const { t } = useI18n()

  // #2549 本番は出さない。古いリンクやブックマークで来ても、空の画面に閉じ込めずホームへ戻す
  if (!isRideEnabled()) return <Navigate to="/home" replace />

  const linkCard = (href: string, title: string, desc: string) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="tap-card flex items-center gap-3 rounded-2xl bg-white p-4 shadow-card"
    >
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-semibold text-text-primary">{title}</p>
        <p className="mt-0.5 font-body text-xs text-text-secondary">{desc}</p>
      </div>
      <ExternalLink size={16} className="shrink-0 text-text-tertiary" />
    </a>
  )

  return (
    <div className="min-h-screen bg-warm-white safe-top pb-10">
      <Header title={t('ride.title')} onBack={() => navigate(-1)} />
      <div className="px-4">
        <p className="mt-4 font-body text-sm text-text-secondary">{t('ride.subtitle')}</p>

        <div className="mt-4 flex flex-col gap-3">
          {linkCard(UBER_PICKUP_URL, t('ride.uber'), t('ride.uberDesc'))}
          {linkCard(TAXI_GO_URL, t('ride.go'), t('ride.goDesc'))}
        </div>

        {/* YOKOSOライド (準備中): 協業後のアプリ内配車のプレビュー */}
        <div className="mt-6 rounded-2xl bg-white p-4 shadow-card">
          <div className="flex items-center gap-2">
            <p className="font-display text-sm font-semibold text-text-primary">{t('ride.own')}</p>
            <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold font-body text-gold">
              {t('ride.preparing')}
            </span>
          </div>
          <p className="mt-1 font-body text-xs leading-relaxed text-text-secondary">
            {t('ride.ownDesc')}
          </p>
          <div className="mt-3 rounded-xl border border-dashed border-gray-300 bg-warm-white p-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <span className="font-body text-xs text-text-tertiary">{t('ride.mockPickup')}</span>
              <span className="font-body text-sm text-text-primary">
                {t('ride.mockCurrentLocation')}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="font-body text-xs text-text-tertiary">{t('ride.mockDropoff')}</span>
              <span className="font-body text-sm text-text-disabled">—</span>
            </div>
            <button
              disabled
              className="mt-2 w-full cursor-not-allowed rounded-xl bg-navy/30 py-3 font-body text-sm font-semibold text-white"
            >
              {t('ride.mockRequest')}
            </button>
          </div>
          <p className="mt-2 font-body text-[11px] leading-relaxed text-text-tertiary">
            {t('ride.mockNote')}
          </p>
        </div>
      </div>
    </div>
  )
}
